import React, { useState, useEffect, useRef, useCallback } from "react";
import { Container, Button, Spinner, Alert } from "reactstrap";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import SelectionBar from "./SelectionBar";
import CancellationModal from "./CancellationModal";
import styles from "../../styles/public/payment-page.module.css";
import api from "../../services/api";
import { STRIPE_PUBLISHABLE_KEY } from "../../constants";
import moment from "moment";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ bookingData, onSuccess, onBack, onEditStep, handleCancel, organization }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const intentCreatedRef = useRef(false);
  const paymentCompletedRef = useRef(false);

  // Parse duration from organization.online_hold_time (format: "HH:MM:SS")
  const timeoutDuration = organization?.online_hold_time 
    ? moment.duration(organization.online_hold_time).asMilliseconds()
    : 5 * 60 * 1000; // Default to 5 minutes if not set
  const timeoutInMinutes = timeoutDuration / (60 * 1000);

  const formattedDate = bookingData.date
    ? bookingData.date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    })
    : null;

  const handleTimeout = useCallback(() => {
    setIsTimeout(true);
    handleCancel();
  }, [handleCancel]);

  useEffect(() => {
    let currentPaymentIntentId = null;

    // Create PaymentIntent as soon as the page loads
    const createPaymentIntent = async () => {
      // Prevent duplicate creation
      if (intentCreatedRef.current) return;
      intentCreatedRef.current = true;

      try {
        const response = await api.post("/stripe/payment-intent/create/", {
          amount: bookingData.total, // Send amount as-is, backend handles cents conversion
          event_guid: bookingData.eventGuid,
          service_id: bookingData.service?.id, // Include service_id for service-based payment options
        });
        setClientSecret(response.data.client_secret);
        setPaymentIntentId(response.data.payment_intent_id);
        currentPaymentIntentId = response.data.payment_intent_id;
      } catch (err) {
        setError("Failed to initialize payment. Please try again.");
        console.error("Error creating payment intent:", err);
        intentCreatedRef.current = false; // Reset on error to allow retry
      }
    };

    if (bookingData.total && bookingData.eventGuid) {
      createPaymentIntent();
    }

    // Cleanup: Cancel payment intent if user navigates away without completing payment
    // Don't cancel if user is just going back to edit booking details
    return () => {
      if (currentPaymentIntentId && !paymentCompletedRef.current) {
        api.post("/stripe/payment-intent/cancel/", {
          payment_intent_id: currentPaymentIntentId,
        }).catch(err => console.error("Error canceling payment intent:", err));
      }
    };
  }, [bookingData.total, bookingData.eventGuid, bookingData.service?.id, bookingData.stripeAccountId]);

  // Timeout to cancel hold after 5 minutes
  useEffect(() => {
    const timeoutId = setTimeout(handleTimeout, timeoutDuration);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [handleTimeout, timeoutDuration]);

  const handleSubmit = async (event) => {
    console.log("Submitting payment...");
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      console.log("Confirming card payment...");
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${bookingData.contactInfo.firstName} ${bookingData.contactInfo.lastName}`,
              email: bookingData.contactInfo.email,
              phone: bookingData.contactInfo.phone,
            },
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);

        // Cancel the payment intent if confirmation failed
        if (paymentIntentId) {
          try {
            await api.post("/stripe/payment-intent/cancel/", {
              payment_intent_id: paymentIntentId,
            });
          } catch (cancelErr) {
            console.error("Error canceling failed payment intent:", cancelErr);
          }
        }
      } else if (paymentIntent.status === "succeeded" || paymentIntent.status === "requires_capture") {
        // Mark payment as completed to prevent cancellation
        paymentCompletedRef.current = true;
        console.log("Payment succeeded with status:", paymentIntent.status);

        // Book the event (change state from Hold to Booked and send email)
        await api.put(`/book/${bookingData.eventGuid}/`, {
          customer_name: `${bookingData.contactInfo.firstName} ${bookingData.contactInfo.lastName}`,
          customer_phone: bookingData.contactInfo.phone,
          customer_email: bookingData.contactInfo.email,
          first_time_user: bookingData.contactInfo.isFirstTime,
          marketing_opt_in: bookingData.contactInfo.wantsEmails,
          notes: bookingData.contactInfo.notes,
          start: bookingData.eventStart,
          end: bookingData.eventEnd,
          resource: bookingData.resource,
          service: bookingData.service.id,
          discount_code: bookingData.contactInfo.discountCode,
          payment_intent_id: paymentIntentId,
          payment_status: paymentIntent.status,
          sendEmail: true,
          state: 'Booked'
        });

        onSuccess({ paymentIntentId });
      } else {
        // Handle unexpected payment status
        console.error("Unexpected payment status:", paymentIntent.status);
        setError(`Payment status: ${paymentIntent.status}. Please contact support.`);
        setProcessing(false);
      }
    } catch (err) {
      setError("Payment failed. Please try again.");
      setProcessing(false);
      console.error("Payment error:", err);

      // Attempt to cancel the payment intent on error
      if (paymentIntentId && !paymentCompletedRef.current) {
        try {
          await api.post("/stripe/payment-intent/cancel/", {
            payment_intent_id: paymentIntentId,
          });
        } catch (cancelErr) {
          console.error("Error canceling payment intent after error:", cancelErr);
        }
      }
    }
  };

  // const handleBack = () => {
  //   // Set flag to prevent cleanup from canceling the payment intent
  //   navigatingBackRef.current = true;
  //   onBack();
  // };

  const handleEditStep = (step) => {
    onEditStep(step);
  };

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Poppins", sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  return (
    <Container className="mb-3 pb-3">
      <SelectionBar
        service={bookingData.service}
        date={formattedDate}
        time={bookingData.time}
        onEditStep={handleEditStep}
      />

      {(isTimeout) ? (
        <Alert color="danger" className="mb-4">
          Your reservation has expired. Please start a new booking.
        </Alert>
      ) : (
        <>
          <div className="text-center mb-4">
            <h2 className={styles["payment-title"]}>Complete your payment</h2>
            <small>(Your hold will expire in {timeoutInMinutes.toFixed(0)} minutes)</small>
            <p className={styles["payment-amount"]}>
              Total: ${parseFloat(bookingData.total).toFixed(2)}
            </p>
          </div>

          {error && (
            <Alert color="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <div className={`${styles["payment-form"]} p-4`}>
            <form onSubmit={handleSubmit}>
              <div className={styles["card-element-wrapper"]}>
                <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  type="button"
                  color="secondary"
                  onClick={onBack}
                  disabled={processing}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className={styles["payment-button"]}
                  disabled={processing || !clientSecret || !cardComplete || isTimeout}
                >
                  {processing ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    `Pay $${parseFloat(bookingData.total).toFixed(2)}`
                  )}
                </Button>
              </div>
            </form>
            {
              bookingData.service?.online_payment_text && (
                <Alert className="mt-4"> 
                  {bookingData.service?.online_payment_text}
                </Alert>
              )
            }
          </div>
        </>
      )}
    </Container>
  );
};

const PaymentPage = ({ bookingData, onSuccess, onBack, onEditStep, handleCancel, organization }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  if (!STRIPE_PUBLISHABLE_KEY) {
    return (
      <Container>
        <Alert color="danger">
          Stripe is not configured. Please contact support.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Elements stripe={stripePromise}>
        <PaymentForm
          bookingData={bookingData}
          onSuccess={onSuccess}
          onBack={onBack}
          onEditStep={onEditStep}
          handleCancel={handleCancel}
          organization={organization}
        />
      </Elements>
      <CancellationModal isOpen={isModalOpen} onClose={toggleModal} />
    </>
  );
};

export default PaymentPage;
